import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'

interface UseImageUploadReturn {
  uploading: boolean
  error: string | null
  uploadImage: (file: File) => Promise<string | null>
  deleteImage: (url: string) => Promise<boolean>
}

export function useImageUpload(): UseImageUploadReturn {
  const { user } = useAuth()
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const uploadImage = async (file: File): Promise<string | null> => {
    if (!user) {
      setError('Must be logged in to upload')
      return null
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('File must be an image')
      return null
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB')
      return null
    }

    setUploading(true)
    setError(null)

    try {
      // Create unique filename
      const ext = file.name.split('.').pop()
      const filename = `${user.id}/${Date.now()}.${ext}`

      const { error: uploadError } = await supabase.storage
        .from('app-images')
        .upload(filename, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (uploadError) throw uploadError

      // Get public URL
      const { data } = supabase.storage
        .from('app-images')
        .getPublicUrl(filename)

      return data.publicUrl
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
      return null
    } finally {
      setUploading(false)
    }
  }

  const deleteImage = async (url: string): Promise<boolean> => {
    if (!url) return true

    try {
      // Extract path from URL
      const path = url.split('/app-images/')[1]
      if (!path) return true

      const { error: deleteError } = await supabase.storage
        .from('app-images')
        .remove([path])

      if (deleteError) throw deleteError
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed')
      return false
    }
  }

  return { uploading, error, uploadImage, deleteImage }
}
